/**
 * Cova Chat — read-model view shapes (plain S.Struct, NOT Model.Class).
 *
 * Two tiers (mirrors feed-views.ts):
 *  - *Row schemas decode the enriched repository SELECTs (joined counterparty
 *    fields, per-viewer unread flags, keyset timestamps). Timestamps are ISO
 *    strings (projected via `to_char(... AT TIME ZONE 'UTC', ...)` in SQL) so
 *    they decode cleanly AND double as keyset cursors.
 *  - *View schemas are the public DTOs the service produces and the frontend's
 *    wire types. Identity display (`counterpartyLabel` / `senderLabel`) is
 *    resolved per-viewer at read time (asymmetric: broker sees the underwriter
 *    BRAND, underwriter sees the broker PERSON), never stored per-viewer.
 *
 * Mailbox IDs NEVER appear in any view — the frontend never sees them.
 */
import { Schema as S } from "effect"
import { ConversationId, ChatMessageId, ConversationKind, ChatParticipantRole } from "./chat-enums"

// ============================================================================
// Repository rows (decode of the enriched SELECTs)
// ============================================================================

/**
 * One conversation row for the inbox list, viewer-scoped. Carries the raw
 * counterparty identity fields (brand + person) so the service can resolve the
 * asymmetric `counterpartyLabel` via the pure `resolveCounterpartyLabel`. The
 * `unread` flag compares the newest other-side message against `last_read_at`.
 */
export const ConversationSummaryRow = S.Struct({
  conversationId: ConversationId,
  kind: ConversationKind,
  // counterparty identity (raw — label resolved in the service, per viewer)
  underwriterName: S.NullOr(S.String), // brand shown to the broker
  brokerFirstName: S.NullOr(S.String),
  brokerLastName: S.NullOr(S.String),
  brokerOrgName: S.NullOr(S.String),
  counterpartyAvatarUrl: S.NullOr(S.String),
  counterpartyWebsite: S.NullOr(S.String), // underwriter website — powers the favicon logo tier
  lastMessagePreview: S.NullOr(S.String),
  lastMessageAt: S.NullOr(S.String), // ISO string (keyset cursor)
  unread: S.Boolean,
  title: S.NullOr(S.String), // group name (null for 1:1 kinds)
  memberCount: S.Number, // participant count (1:1 = 2)
  muted: S.Boolean, // the CALLER's own participant mute flag
  favourited: S.Boolean, // the CALLER's own participant favourite flag (pin-to-top)
  archived: S.Boolean, // the CALLER's own participant archive flag (hidden from inbox)
})
export type ConversationSummaryRow = typeof ConversationSummaryRow.Type

/**
 * One message row for a thread page, viewer-scoped. `senderMailboxId` is NOT
 * projected (never leaves the backend); the service derives `isMine` and the
 * per-viewer `senderLabel` from the joined sender identity fields.
 */
export const MessageRow = S.Struct({
  messageId: ChatMessageId,
  body: S.String,
  // sender identity (raw — label + isMine resolved in the service, per viewer)
  senderUserId: S.NullOr(S.String),
  senderUnderwriterName: S.NullOr(S.String),
  senderFirstName: S.NullOr(S.String),
  senderLastName: S.NullOr(S.String),
  senderOrgName: S.NullOr(S.String),
  isMine: S.Boolean,
  createdAt: S.String, // ISO
})
export type MessageRow = typeof MessageRow.Type

// BIGINT (size_bytes) ships as a STRING from pg-protocol by default (the app's
// SqlClientLayer only coerces date OIDs), so decode string-or-number — same
// pattern the files context uses for its own size_bytes columns.
const BigIntAsNumber = S.Union(S.Number, S.NumberFromString)

/** One attachment row under a message. `url` is a lazily-signed download URL. */
export const AttachmentRow = S.Struct({
  messageId: ChatMessageId,
  fileNodeId: S.String,
  filename: S.String, // PII — never logged
  mime: S.String,
  sizeBytes: BigIntAsNumber,
})
export type AttachmentRow = typeof AttachmentRow.Type

// ============================================================================
// Public DTOs (service output → RPC success)
// ============================================================================

export const MessageAttachmentView = S.Struct({
  fileNodeId: S.String,
  filename: S.String,
  mime: S.String,
  sizeBytes: S.Number,
  url: S.NullOr(S.String),
})
export type MessageAttachmentView = typeof MessageAttachmentView.Type

/** One shared file across the whole conversation (the "Content" tab). `senderLabel`
 *  is the uploading member's display name; `createdAt` is the attach time (ISO).
 *  `sizeBytes` decodes the BIGINT string-or-number the same way the rows do. */
export const ConversationAttachmentView = S.Struct({
  fileNodeId: S.String,
  filename: S.String, // PII — never logged
  mime: S.String,
  sizeBytes: BigIntAsNumber,
  senderLabel: S.String,
  createdAt: S.String, // ISO
})
export type ConversationAttachmentView = typeof ConversationAttachmentView.Type

/** ASYMMETRIC counterparty: brand for the broker view, person for the uw view.
 *  Groups have no single counterparty — they render `title`; `counterpartyLabel`
 *  is null and `memberCount` carries the participant total. */
export const ConversationSummaryView = S.Struct({
  conversationId: ConversationId,
  kind: ConversationKind,
  counterpartyLabel: S.NullOr(S.String), // null for groups (render `title` instead)
  counterpartyAvatarUrl: S.NullOr(S.String),
  // When the 1:1 counterparty is an underwriter, the frontend renders the shared
  // `UnderwriterLogo` (logo → favicon-from-domain → initials) for marketplace
  // parity. `counterpartyDomain` is the bare host (www-stripped) for the favicon
  // tier; both are absent for user counterparties and multi-party kinds.
  counterpartyIsUnderwriter: S.Boolean,
  counterpartyDomain: S.NullOr(S.String),
  lastMessagePreview: S.NullOr(S.String),
  lastMessageAt: S.NullOr(S.String), // ISO string (keyset cursor)
  unread: S.Boolean,
  title: S.NullOr(S.String), // group name (null for 1:1 kinds)
  memberCount: S.Number, // participant count (1:1 = 2)
  muted: S.Boolean, // the caller has muted this conversation (in-app notifications silenced)
  favourited: S.Boolean, // the caller has starred this conversation (pinned to top)
  archived: S.Boolean, // the caller has archived this conversation (hidden from the inbox)
})
export type ConversationSummaryView = typeof ConversationSummaryView.Type

/** Extra contact details for a 1:1 counterparty (the Direct → Profile tab),
 *  fetched on demand because email/contact are PII and don't belong in every
 *  inbox row. All fields are best-effort — null when the source has no value.
 *  `role` is "Admin"/"Member" for teammates; `orgName` is their organisation. */
export const ConversationProfileView = S.Struct({
  email: S.NullOr(S.String), // PII — never logged
  contact: S.NullOr(S.String), // phone/mobile — PII, never logged
  role: S.NullOr(S.String),
  orgName: S.NullOr(S.String),
})
export type ConversationProfileView = typeof ConversationProfileView.Type

/** One mention rendered inline; `label` is the mentioned member's display name. */
export const MentionView = S.Struct({
  userId: S.String,
  label: S.String,
})
export type MentionView = typeof MentionView.Type

export const MessageView = S.Struct({
  messageId: ChatMessageId,
  body: S.String,
  senderLabel: S.String, // resolved per viewer (brand vs person)
  isMine: S.Boolean,
  attachments: S.Array(MessageAttachmentView),
  mentions: S.Array(MentionView), // members @-mentioned in this message
  mentionsMe: S.Boolean, // viewer was mentioned (drives highlight)
  createdAt: S.String, // ISO
})
export type MessageView = typeof MessageView.Type

/** A page of a thread (keyset on createdAt+id). */
export const MessagePage = S.Struct({
  items: S.Array(MessageView),
  nextCursor: S.NullOr(S.String),
})
export type MessagePage = typeof MessagePage.Type

/** Unread signal for the nav bell badge. */
export const ChatUnreadStatus = S.Struct({
  unreadConversations: S.Number,
  hasUnread: S.Boolean,
})
export type ChatUnreadStatus = typeof ChatUnreadStatus.Type

/** A teammate the caller can start a team DM with (team-chat picker). `userId`
 *  is the canonical users.id. */
export const ChatTeammate = S.Struct({
  userId: S.String,
  name: S.String,
  email: S.NullOr(S.String),
  imageUrl: S.NullOr(S.String),
})
export type ChatTeammate = typeof ChatTeammate.Type

/** Group member row for the member list + mention autocomplete. `isMe` is
 *  computed per viewer (userId === identity.covaUserId), not stored. */
export const GroupMemberView = S.Struct({
  userId: S.String,
  name: S.String,
  role: ChatParticipantRole,
  isMe: S.Boolean,
})
export type GroupMemberView = typeof GroupMemberView.Type

/**
 * Result of marking a conversation read. `previousLastReadAt` is the last-read
 * timestamp BEFORE this mark (null on first read) — the client uses it as the
 * unread boundary. Returning it from the same statement that writes the new
 * value makes the boundary race-free (mirrors FeedMarkSeenResult).
 */
export const ChatMarkReadResult = S.Struct({
  ok: S.Boolean,
  previousLastReadAt: S.NullOr(S.String),
})
export type ChatMarkReadResult = typeof ChatMarkReadResult.Type
